import arraySort from 'array-sort';
import { isBefore } from 'date-fns';
import { IntlShape } from 'react-intl';

import { SortRules } from '../../domain';
import { InTripReview } from '../in-trip-review/types';
import { PreTripReview } from '../pre-trip-review/types';
import { AvailableLocale } from '../../shared/intl/types';
import { getLocaleCode } from '../../shared/intl/operations';

import {
    AvailableTasks,
    CustomerCareCrisisTicket,
    DeferAgreement,
    DisplayDossierOverview,
    DisplayDossierOverviews,
    DossierOverview,
    DossierOverviewWithInbox,
    ExchangeDiscussion,
    Memo,
    Participants,
    TaskDefinition,
    Voucher,
    TravellerInformation,
} from './types';

export function sortDossierOverviews(
    dossierOverviews: DisplayDossierOverviews,
    sortRules: SortRules,
): DisplayDossierOverviews {
    return arraySort(dossierOverviews, sortRules);
}

export const displayDossierOverviewsToIds = (dossierOverviews: DisplayDossierOverviews): string[] =>
    dossierOverviews.map((value) => value.id);

export const displayDossierOverviewsToTaskDefinitions = (
    dossierOverviews: DisplayDossierOverviews,
): TaskDefinition[] => dossierOverviews.map((value) => value.task.definition);

export const isLastMessageFromTraveller = ({
    discussion: { lastAgentToTravellerMessageDate, lastTravellerToAgentMessageDate },
}: {
    discussion: ExchangeDiscussion;
}): boolean =>
    // There is no message from agent and there is one from traveller
    (!lastAgentToTravellerMessageDate && !!lastTravellerToAgentMessageDate) ||
    // or latest message is from traveller
    (!!lastTravellerToAgentMessageDate &&
        !!lastAgentToTravellerMessageDate &&
        isBefore(lastAgentToTravellerMessageDate, lastTravellerToAgentMessageDate));

export const hasUnreadMessage = ({
    discussion: {
        lastAgentToTravellerMessageDate,
        lastTravellerToAgentMessageDate,
        lastMessageReadDate,
    },
}: {
    discussion: ExchangeDiscussion;
}): boolean =>
    !!lastMessageReadDate &&
    !!lastTravellerToAgentMessageDate &&
    !!lastAgentToTravellerMessageDate &&
    isBefore(lastAgentToTravellerMessageDate, lastTravellerToAgentMessageDate) &&
    isBefore(lastMessageReadDate, lastTravellerToAgentMessageDate);

export const generateUploadVouchersFormData = (files: File[], deferDate: string): FormData => {
    const formData = new window.FormData();
    for (const voucher of files) {
        formData.append('vouchers[]', voucher);
    }
    formData.append('due-date', deferDate);
    return formData;
};

export const formatDossierOverview = ({
    dossierOverview,
    memo,
    participants,
    availableTasks,
    customerCareCrisisTicket,
    vouchers,
    deferAgreement,
    preTripReview,
    inTripReview,
    travellerInformation,
}: {
    dossierOverview: DossierOverview | DossierOverviewWithInbox;
    memo?: Memo | null;
    participants?: Participants | null;
    availableTasks?: AvailableTasks | null;
    customerCareCrisisTicket?: CustomerCareCrisisTicket | null;
    vouchers?: Voucher[] | null;
    deferAgreement?: DeferAgreement | null;
    preTripReview?: PreTripReview | null;
    inTripReview?: InTripReview | null;
    travellerInformation?: TravellerInformation | null;
}): DisplayDossierOverview => ({
    ...dossierOverview,
    memo,
    participants,
    availableTasks,
    customerCareCrisisTicket,
    vouchers,
    deferAgreement,
    preTripReview,
    inTripReview,
    travellerInformation,
});

export const formatBudgetPerDayPerPax = (
    budgetPerDayPerPax: number | null,
    currency: string | null,
    formatMessage: IntlShape['formatMessage'],
    locale: AvailableLocale,
): string | undefined => {
    if (!budgetPerDayPerPax || !currency) return undefined;
    return formatMessage(
        { id: 'dossier.information.traveller.trips.budget_value' },
        {
            budget: Number(budgetPerDayPerPax / 100).toLocaleString(getLocaleCode(locale), {
                style: 'currency',
                currency: currency,
                maximumFractionDigits: 0,
                currencyDisplay: 'symbol',
            }),
        },
    );
};
